import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
} from '@chakra-ui/react';
import React, { useState } from 'react';
import { FaCreditCard, FaEllipsisV, FaMoneyBillWave, FaUndoAlt } from 'react-icons/fa';
import { translate, TranslationKeys } from '../../../i18n';
import { PaymentIntentDto, PaymentIntentStatus } from '../../../models/payment/dto/payment-intent.dto';
import { useGetPaymentIntentsForCoachingSession } from '../hooks/useGetPaymentIntentsForCoachingSession';
import { useChargeCoachingPayLater } from '../hooks/useChargeCoachingPayLater';
import { useMarkManualPaymentRefunded } from '../../game-details/hooks/useMarkManualPaymentRefunded';
import { useMarkPayLaterAsPaid } from '../../game-details/hooks/useMarkPayLaterAsPaid';
import { formatCoachingPaymentAmount, getCoachingPaymentColorScheme, getCoachingPaymentLabelKey } from '../utils/coaching-payment.utils';
import { formatDate } from '../CoachingSessionDetailsPage';

interface PaymentIntentsProps {
  coachingSessionId: string;
}

const LABEL_KEY_TO_TRANSLATION: Record<string, string> = {
  pay_later: 'game.payment_pay_later',
  completed: 'game.payment_completed',
  pending: 'game.payment_pending',
  cancelled: 'game.payment_cancelled',
  failed: 'game.payment_failed',
};

const getPaymentStatusBadge = (paymentIntent: PaymentIntentDto) => {
  if (paymentIntent.paymentMethodType === 'manual_card') {
    return <Badge colorScheme="green">{translate('game.payment_manual_card' as TranslationKeys)}</Badge>;
  }
  if (paymentIntent.paymentMethodType === 'manual_cash') {
    return <Badge colorScheme="green">{translate('game.payment_manual_cash' as TranslationKeys)}</Badge>;
  }
  const colorScheme = getCoachingPaymentColorScheme(paymentIntent.status, paymentIntent.isSetupIntentPayment);
  const labelKey = getCoachingPaymentLabelKey(paymentIntent.status, paymentIntent.isSetupIntentPayment);
  return <Badge colorScheme={colorScheme}>{translate(LABEL_KEY_TO_TRANSLATION[labelKey] as TranslationKeys)}</Badge>;
};

const PaymentIntents: React.FC<PaymentIntentsProps> = ({ coachingSessionId }) => {
  const { data: paymentIntents, isLoading, error } = useGetPaymentIntentsForCoachingSession(coachingSessionId);
  const chargeMutation = useChargeCoachingPayLater(coachingSessionId);
  const markPaidMutation = useMarkPayLaterAsPaid();
  const markRefundedMutation = useMarkManualPaymentRefunded();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedPI, setSelectedPI] = useState<PaymentIntentDto | null>(null);
  const [chargeAmount, setChargeAmount] = useState<number>(0);

  const handleChargeClick = (pi: PaymentIntentDto) => {
    setSelectedPI(pi);
    setChargeAmount(pi.amount / 100);
    onOpen();
  };

  const handleConfirmCharge = () => {
    if (!selectedPI) return;
    chargeMutation.mutate(
      {
        paymentIntentId: selectedPI.id,
        options: { customAmount: Math.round(chargeAmount * 100) },
      },
      { onSettled: onClose },
    );
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
        <Spinner size="md" />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error">
        <AlertIcon />
        {translate('coaching_session.error_loading_payment_intents')}
      </Alert>
    );
  }

  if (!paymentIntents || paymentIntents.length === 0) {
    return (
      <Box textAlign="center" py={8}>
        <Text fontSize="lg" color="gray.500">
          {translate('coaching_session.no_payment_intents')}
        </Text>
      </Box>
    );
  }

  return (
    <>
      <TableContainer>
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>{translate('game.joined_at')}</Th>
              <Th>{translate('game.payment_status')}</Th>
              <Th isNumeric>{translate('game.payment_amount')}</Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {paymentIntents.map((paymentIntent) => (
              <Tr key={paymentIntent.id}>
                <Td>
                  <Text fontSize="sm">{formatDate(paymentIntent.createdAt)}</Text>
                </Td>
                <Td>{getPaymentStatusBadge(paymentIntent)}</Td>
                <Td isNumeric>
                  <Text fontSize="sm">
                    {formatCoachingPaymentAmount(paymentIntent.amount, paymentIntent.currency)}
                  </Text>
                </Td>
                <Td>
                  {paymentIntent.isSetupIntentPayment &&
                    paymentIntent.status === PaymentIntentStatus.pay_later && (
                      <Menu strategy="fixed">
                        <MenuButton
                          as={IconButton}
                          aria-label={translate('game.actions' as TranslationKeys)}
                          icon={<FaEllipsisV />}
                          variant="ghost"
                          size="sm"
                          isLoading={chargeMutation.isLoading || markPaidMutation.isLoading}
                        />
                        <MenuList>
                          <MenuItem
                            icon={<FaCreditCard />}
                            onClick={() => handleChargeClick(paymentIntent)}
                          >
                            {translate('game.charge_pay_later' as TranslationKeys)}
                          </MenuItem>
                          <MenuItem
                            icon={<FaCreditCard />}
                            onClick={() => {
                              if (window.confirm(translate('game.mark_paid_confirm_message' as TranslationKeys))) {
                                markPaidMutation.mutate({
                                  paymentIntentId: paymentIntent.id,
                                  method: 'manual_card',
                                });
                              }
                            }}
                          >
                            {translate('game.mark_paid_card' as TranslationKeys)}
                          </MenuItem>
                          <MenuItem
                            icon={<FaMoneyBillWave />}
                            onClick={() => {
                              if (window.confirm(translate('game.mark_paid_confirm_message' as TranslationKeys))) {
                                markPaidMutation.mutate({
                                  paymentIntentId: paymentIntent.id,
                                  method: 'manual_cash',
                                });
                              }
                            }}
                          >
                            {translate('game.mark_paid_cash' as TranslationKeys)}
                          </MenuItem>
                        </MenuList>
                      </Menu>
                    )}
                  {paymentIntent.paymentMethodType?.startsWith('manual_') &&
                    paymentIntent.status === PaymentIntentStatus.succeeded && (
                      <Tooltip label={translate('game.mark_refunded' as TranslationKeys)}>
                        <IconButton
                          aria-label={translate('game.mark_refunded' as TranslationKeys)}
                          icon={<FaUndoAlt />}
                          colorScheme="orange"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (window.confirm(translate('game.mark_refunded_confirm' as TranslationKeys))) {
                              markRefundedMutation.mutate(paymentIntent.id);
                            }
                          }}
                          isLoading={markRefundedMutation.isLoading}
                        />
                      </Tooltip>
                    )}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Encaisser la session de coaching</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedPI && (
              <>
                <Text mb={2}>
                  Montant initial : {formatCoachingPaymentAmount(selectedPI.amount, selectedPI.currency)}
                </Text>
                <Text mb={2} fontWeight="medium">Montant a encaisser :</Text>
                <NumberInput
                  value={chargeAmount}
                  onChange={(_, val) => setChargeAmount(val || 0)}
                  min={0.01}
                  max={selectedPI.amount / 100}
                  precision={2}
                  step={0.5}
                >
                  <NumberInputField />
                </NumberInput>

                <Alert status="warning" mt={4} borderRadius="md">
                  <AlertIcon />
                  <Text fontSize="sm">
                    Cette action debitera la carte du joueur sans action de sa
                    part. Assurez-vous que la session a bien ete dispensee.
                  </Text>
                </Alert>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Annuler
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleConfirmCharge}
              isLoading={chargeMutation.isLoading}
            >
              Encaisser {chargeAmount.toFixed(2)} {selectedPI?.currency?.toUpperCase() || 'EUR'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default PaymentIntents;
